import { formatVolumeCap, volumeCapPayload, UNCAPPED_LABEL } from "utilities/volumeCap";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CredentialCreateModal from "pages/integrations/CredentialCreateModal";
import PageHeader from "components/PageLayout";
import logoBSquare from "assets/img/logo_b_square.png";
import {
  ArrowLeft,
  CreditCard,
  Clock,
  Settings,
  Link2,
  Unlink,
  Layers,
  Shield,
  Plus,
  Pencil,
  Save,
  X,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  XCircle,
  Info,
  KeyRound,
  ExternalLink,
  Loader2,
  Trash2,
} from "lucide-react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth, requireAuth } from "utilities/auth";
import { isInternal } from "utilities/userType";
import { useToast } from "components/Toast";
import { InfoField } from "components/Account/InfoField";
import Skeleton from "components/Skeleton";
import SourceBadge from "components/SourceBadge";
import ConfirmDialog from "components/ConfirmDialog";
import { Building2, Cpu } from "lucide-react";
import RecordStatusPill from "components/Records/RecordStatusPill";
import RecordTable from "components/Records/RecordTable";
import RelativeTime from "components/RelativeTime";
import RecordTimeline from "components/Records/RecordTimeline";
import { buildStatusTimeline } from "utilities/recordTimeline";
import { MID_STATUS, CREDENTIAL_KIND, statusLabelFrom, statusDotFrom, statusToneFrom } from "utilities/statusTones";
import { hasSecretStorage } from "utilities/credentialFields";
import EntityTicketsSection from "components/Tickets/EntityTicketsSection";
import RecordLinksPanel from "components/Records/RecordLinksPanel";

// Same tone scale as /credentials and the client tab — a credential type
// must not be styled one way here and another way there.
const credentialKindTone = statusToneFrom(CREDENTIAL_KIND);

const MID_MANAGER_TRANSITIONS = {
  active: ["suspended", "closed"],
  suspended: ["active", "closed"],
  closed: [],
};

// Status labels come from utilities/statusTones so the page, the pill and the
// timeline cannot disagree. This file used to keep its own identical copy.
const midStatusLabel = statusLabelFrom(MID_STATUS);

function MidDetail() {
  const { midId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = isInternal(user);
  const { toast } = useToast();

  const [mid, setMid] = useState(null);
  const [loading, setLoading] = useState(true);

  // Status change state
  const [selectedStatus, setSelectedStatus] = useState("");
  const [statusReason, setStatusReason] = useState("");
  const [submitting, setSubmitting] = useState(false);


  // Disputifier state
  const [disputifierData, setDisputifierData] = useState(null);
  const [disputifierLoading, setDisputifierLoading] = useState(true);
  const [showEnrollForm, setShowEnrollForm] = useState(false);
  const [enrollForm, setEnrollForm] = useState({
    dbaId: "",
    email: "",
    thresholdAmount: "",
    programs: { rdr: true, cdrn: true, ethoca: true, tc40: true },
  });
  const [dbas, setDbas] = useState([]);
  const [syncing, setSyncing] = useState(false);

  // Category-driven integration state (replaces NMI-specific state)
  const [integrations, setIntegrations] = useState([]);
  const [integrationsLoading, setIntegrationsLoading] = useState(true);
  const [showProvisionForm, setShowProvisionForm] = useState(null); // "gateway" | "crm" | null
  const [provisionForm, setProvisionForm] = useState({ provider: "", credentialId: "" });
  const [provisionCredentials, setProvisionCredentials] = useState([]);
  const [provisionSyncing, setProvisionSyncing] = useState(null); // integrationId being synced

  // Link-existing state (NMI gateway/processor — SOUND-343 rework)
  const [showLinkForm, setShowLinkForm] = useState(null); // categoryType | null
  const [linkForm, setLinkForm] = useState({ credentialId: "", merchantId: "", processorId: "" });

  const [linkCredentials, setLinkCredentials] = useState([]);
  const [linkGateways, setLinkGateways] = useState([]);
  const [linkGatewaysLoading, setLinkGatewaysLoading] = useState(false);
  const [linkCandidates, setLinkCandidates] = useState([]);
  const [linkCandidatesLoading, setLinkCandidatesLoading] = useState(false);
  const [linkSubmitting, setLinkSubmitting] = useState(false);

  // Link Existing — Disputifier (SOUND-458): live merchant/MID picker
  const [showDispLink, setShowDispLink] = useState(false);
  const [dispSearch, setDispSearch] = useState("");
  const [dispMerchants, setDispMerchants] = useState([]);
  const [dispMerchantsLoading, setDispMerchantsLoading] = useState(false);
  const [dispMerchant, setDispMerchant] = useState(null);
  const [dispCandidates, setDispCandidates] = useState([]);
  const [dispCandidatesLoading, setDispCandidatesLoading] = useState(false);
  const [dispMidId, setDispMidId] = useState("");
  const [dispLinkSubmitting, setDispLinkSubmitting] = useState(false);

  // Link Existing — CRM (CC/PHX) from the risk /unresolved worklist (SOUND-458).
  // No CheckoutChamp/Phoenix API access, so candidates come from the mart worklist.
  const [showCrmLink, setShowCrmLink] = useState(false);
  const [crmSearch, setCrmSearch] = useState("");
  const [crmCandidates, setCrmCandidates] = useState([]);
  const [crmCandidatesLoading, setCrmCandidatesLoading] = useState(false);
  const [crmSelected, setCrmSelected] = useState(null);
  const [crmLinkSubmitting, setCrmLinkSubmitting] = useState(false);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: "", description: "", onConfirm: () => {} });
  const closeConfirmDialog = useCallback(() => setConfirmDialog(c => ({ ...c, open: false })), []);

  // Edit fields state
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  // Credentials state
  const [midCredentials, setMidCredentials] = useState([]);
  const [midCredentialsLoading, setMidCredentialsLoading] = useState(true);
  const [showCredentialModal, setShowCredentialModal] = useState(false);
  const [editingCredential, setEditingCredential] = useState(null);
  const [showCreateCredential, setShowCreateCredential] = useState(false);
  const [credentialForm, setCredentialForm] = useState({
    provider: "",
    label: "",
    visibility: "internal",
    portal: { url: "", username: "", has2fa: false, notes: "" },
    secrets: [{ key: "", value: "" }],
  });
  const [credentialSubmitting, setCredentialSubmitting] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await makeApiRequest(
        "get",
        apiRoutes.getMidById(midId),
        { id: user.id, accessToken: user.accessToken },
        user.companyId
      );
      setMid(res.data?.data || null);
    } catch (err) {
      console.error("[MidDetail] Failed to fetch MID", err);
      toast({
        status: "destructive",
        title: "Failed to load MID",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchDisputifier = async () => {
    setDisputifierLoading(true);
    try {
      const res = await makeApiRequest(
        "get",
        apiRoutes.disputifierGetEnrollments(midId),
        { id: user.id, accessToken: user.accessToken },
        user.companyId
      );
      setDisputifierData(res.data?.data || null);
    } catch (err) {
      // 404 = not enrolled, which is fine
      if (err.response?.status !== 404) {
        console.error("[MidDetail] Disputifier fetch failed", err);
      }
      setDisputifierData(null);
    } finally {
      setDisputifierLoading(false);
    }
  };

  // MID-level column model. Deliberately narrower than the global list: CORP,
  // DBA, PROCESSOR and MID NUMBER are all fixed on this page, so repeating them
  // per row would be noise. Same components and same meanings as /credentials.
  const midCredentialColumns = useMemo(
    () => [
      {
        key: "label",
        width: "26%",
        header: "Credential",
        cell: (c) => <div className="truncate font-medium">{c.label || "—"}</div>,
      },
      { key: "provider", width: "14%", header: "Provider",
        cell: (c) => <span className="capitalize">{c.provider || "—"}</span> },
      { key: "kind", width: "12%", header: "Type",
        cell: (c) => <RecordStatusPill status={c.credentialType} toneFor={credentialKindTone} /> },
      {
        key: "storage",
        width: "18%",
        header: "Secret storage",
        // The old table had a "Secrets" column that rendered a dash for every
        // row regardless of what was stored — a column that never said anything.
        cell: (c) =>
          hasSecretStorage(c) ? (
            <span className="text-xs text-muted-foreground">{Object.keys(c.vault.fields).join(", ")}</span>
          ) : (
            <span className="badge badge-warning" title="No secret storage — the password has nowhere to live">None</span>
          ),
      },
      { key: "2fa", width: "8%", header: "2FA",
        cell: (c) => (c.portal?.has2fa ? <span className="badge badge-warning">Yes</span> : <span className="text-xs text-muted-foreground">No</span>) },
      { key: "lastAccess", width: "12%", header: "Last used",
        cell: (c) => c.lastAccess?.at
          ? <span className="text-xs text-muted-foreground"><RelativeTime value={c.lastAccess.at} /></span>
          : <span className="text-xs text-muted-foreground">Never</span> },
      isAdmin && {
        key: "actions",
        width: "10%",
        header: "",
        cell: (c) => (
          <div className="flex justify-end gap-1">
            <button
              className="btn btn-ghost h-7 w-7 p-0"
              title="Edit"
              onClick={(e) => { e.stopPropagation(); openEditCredentialModal(c); }}
            >
              <Pencil className="h-4 w-4" />
            </button>
            {/* The old table carried Deactivate and the first replacement
                dropped it. Rebuilding a surface means enumerating what it DID,
                not only what the new one should look like — the same mistake
                that deleted the create flow. */}
            <button
              className="btn btn-ghost h-7 w-7 p-0 text-destructive"
              title="Deactivate"
              onClick={(e) => { e.stopPropagation(); handleDeactivateCredential(c); }}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ),
      },
    ],
    // handleDeactivateCredential and openEditCredentialModal are stable for the
    // life of the component; listing them would rebuild the column model on
    // every render to no effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAdmin]
  );

  const fetchMidCredentials = async () => {
    setMidCredentialsLoading(true);
    try {
      const res = await makeApiRequest(
        "get",
        apiRoutes.getMidCredentials(midId),
        { id: user.id, accessToken: user.accessToken },
        user.companyId
      );
      setMidCredentials(res.data?.data || []);
    } catch {
      setMidCredentials([]);
    } finally {
      setMidCredentialsLoading(false);
    }
  };

  const fetchDbas = async (companyId) => {
    try {
      const res = await makeApiRequest(
        "get",
        apiRoutes.getApplication(companyId),
        { id: user.id, accessToken: user.accessToken },
        user.companyId
      );
      setDbas(res.data?.data?.dbas || res.data?.dbas || []);
    } catch {
      setDbas([]);
    }
  };

  const fetchIntegrations = async () => {
    setIntegrationsLoading(true);
    try {
      const res = await makeApiRequest(
        "get",
        apiRoutes.getMidIntegrations(midId),
        { id: user.id, accessToken: user.accessToken },
        user.companyId
      );
      setIntegrations(res.data?.data || []);
    } catch {
      setIntegrations([]);
    } finally {
      setIntegrationsLoading(false);
    }
  };

  const fetchProvisionCredentials = async (provider) => {
    try {
      const res = await makeApiRequest(
        "get",
        `${apiRoutes.getCredentials}?provider=${provider}&credentialType=api`,
        { id: user.id, accessToken: user.accessToken },
        user.companyId
      );
      setProvisionCredentials(res.data?.data || []);
    } catch {
      setProvisionCredentials([]);
    }
  };

  useEffect(() => {
    if (midId) {
      fetchData();
      fetchDisputifier();
      fetchIntegrations();
      fetchMidCredentials();
    }
  }, [midId]);

  // --- Status Change ---
  const handleStatusChange = async () => {
    if (!selectedStatus) return;
    setSubmitting(true);
    try {
      await makeApiRequest(
        "put",
        apiRoutes.changeMidStatus(midId),
        { id: user.id, accessToken: user.accessToken },
        user.companyId,
        { status: selectedStatus, reason: statusReason || undefined }
      );
      toast({
        status: "success",
        title: `Status changed to ${midStatusLabel(selectedStatus)}`,
      });
      setSelectedStatus("");
      setStatusReason("");
      await fetchData();
    } catch (err) {
      toast({
        status: "destructive",
        title: "Failed to change status",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // --- Edit MID Fields ---
  const startEditing = () => {
    setEditForm({
      name: mid.name || "",
      // SOUND-642: five API-editable fields had no input at all. `effectiveDate`
      // and `chargebackThreshold` were capturable NOWHERE in the product — not
      // on create either — which is why effectiveDate is null on all 261 MIDs.
      mid: mid.mid || "",
      effectiveDate: mid.effectiveDate
        ? new Date(mid.effectiveDate).toISOString().slice(0, 10)
        : "",
      chargebackThreshold:
        mid.chargebackThreshold != null ? String(mid.chargebackThreshold) : "",
      currencies: (mid.currencies || []).join(", "),
      multiCurrency: Boolean(mid.multiCurrency),
      descriptor: mid.descriptor || "",
      mcc: mid.mcc || "",
      caid: mid.caid || "",
      acquiringBank: mid.acquiringBank || "",
      bin: mid.bin || "",
      notes: mid.notes || "",
      tags: (mid.tags || []).join(", "),
      // SOUND-657: uncapped is the STATE, read from the record — not inferred
      // from a missing number. A blank cap is $0, and a $0 MID must not open
      // the edit form pre-ticked as uncapped.
      monthlyVolumeCap:
        mid.monthlyVolumeCap != null ? String(mid.monthlyVolumeCap) : "",
      isUncapped: Boolean(mid.isUncapped),
      // Default true for legacy MIDs saved before the field existed.
      isSoundOwned: mid.isSoundOwned !== false,
      reserveType: mid.reserve?.type || "none",
      reservePercentage: mid.reserve?.percentage ?? "",
      reserveHoldPeriod: mid.reserve?.holdPeriod ?? "",
      reserveCap: mid.reserve?.cap ?? "",
      reserveNotes: mid.reserve?.notes || "",
    });
    setEditing(true);
  };

  const handleSaveEdit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        name: editForm.name?.trim() || null,
        mid: editForm.mid?.trim() || null,
        // Sent as a date-only string; the API stores what it is given and the
        // read view formats it. Blank clears the field rather than writing "".
        effectiveDate: editForm.effectiveDate || null,
        chargebackThreshold:
          editForm.chargebackThreshold === "" ||
          editForm.chargebackThreshold == null
            ? null
            : Number(editForm.chargebackThreshold),
        currencies: editForm.currencies
          ? editForm.currencies.split(",").map((c) => c.trim()).filter(Boolean)
          : [],
        multiCurrency: Boolean(editForm.multiCurrency),
        descriptor: editForm.descriptor,
        mcc: editForm.mcc,
        caid: editForm.caid,
        acquiringBank: editForm.acquiringBank || null,
        bin: editForm.bin || null,
        notes: editForm.notes || null,
        tags: editForm.tags
          ? editForm.tags.split(",").map((t) => t.trim()).filter(Boolean)
          : [],
        // SOUND-657: one place settles the mutually exclusive pair.
        ...volumeCapPayload(editForm),
        isSoundOwned: editForm.isSoundOwned,
        reserve: {
          type: editForm.reserveType,
          percentage: editForm.reservePercentage ? Number(editForm.reservePercentage) : null,
          holdPeriod: editForm.reserveHoldPeriod ? Number(editForm.reserveHoldPeriod) : null,
          cap: editForm.reserveCap ? Number(editForm.reserveCap) : null,
          notes: editForm.reserveNotes || null,
        },
      };
      await makeApiRequest(
        "put",
        apiRoutes.updateMid(midId),
        { id: user.id, accessToken: user.accessToken },
        user.companyId,
        payload
      );
      toast({ status: "success", title: "MID updated" });
      setEditing(false);
      await fetchData();
    } catch (err) {
      toast({
        status: "destructive",
        title: "Failed to update MID",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // --- Disputifier Actions ---
  const handleEnrollDisputifier = async () => {
    if (!enrollForm.dbaId) return;
    setSubmitting(true);
    try {
      await makeApiRequest(
        "post",
        apiRoutes.disputifierEnrollMerchant,
        { id: user.id, accessToken: user.accessToken },
        user.companyId,
        {
          companyId: mid.companyId,
          dbaId: enrollForm.dbaId,
          midId,
          email: enrollForm.email || undefined,
          thresholdAmount: enrollForm.thresholdAmount ? Number(enrollForm.thresholdAmount) : undefined,
          programs: enrollForm.programs,
        }
      );
      toast({ status: "success", title: "MID enrolled in Disputifier" });
      setShowEnrollForm(false);
      await fetchDisputifier();
    } catch (err) {
      toast({
        status: "destructive",
        title: "Enrollment failed",
        description: err.response?.data?.error || err.response?.data?.message || "Unknown error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSyncDisputifier = async () => {
    setSyncing(true);
    try {
      await makeApiRequest(
        "post",
        apiRoutes.disputifierSyncMid(midId),
        { id: user.id, accessToken: user.accessToken },
        user.companyId
      );
      toast({ status: "success", title: "Enrollment data synced" });
      await fetchDisputifier();
    } catch (err) {
      toast({
        status: "destructive",
        title: "Sync failed",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleCloseDisputifier = () => {
    setConfirmDialog({
      open: true,
      title: "Close Disputifier MID",
      description: "Close this MID from Disputifier? This will unenroll from all programs.",
      confirmLabel: "Close",
      variant: "destructive",
      onConfirm: async () => {
        closeConfirmDialog();
        setSubmitting(true);
        try {
          await makeApiRequest(
            "put",
            apiRoutes.disputifierCloseMid(midId),
            { id: user.id, accessToken: user.accessToken },
            user.companyId
          );
          toast({ status: "success", title: "MID closed in Disputifier" });
          await fetchDisputifier();
          await fetchData();
        } catch (err) {
          toast({
            status: "destructive",
            title: "Close failed",
            description: err.response?.data?.error || "Unknown error",
          });
        } finally {
          setSubmitting(false);
        }
      },
    });
  };

  // --- Provider Config Map ---
  const PROVIDER_CONFIG = {
    nmi: {
      label: "NMI",
      type: "gateway",
      provisionRoute: apiRoutes.nmiProvision,
      syncRoute: (id) => apiRoutes.nmiSync(id),
      // no deactivateRoute — see note above renderIntegrationTiles
      extraFields: [],
    },
    checkoutchamp: {
      label: "Checkout Champ",
      type: "crm",
      provisionRoute: apiRoutes.checkoutchampProvision,
      syncRoute: (id) => apiRoutes.checkoutchampUpdate(id),
      // no deactivateRoute — see note above renderIntegrationTiles
      extraFields: [],
    },
    phoenix: {
      label: "Phoenix",
      type: "crm",
      extraFields: [],
    },
  };

  const CATEGORY_PROVIDERS = {
    gateway: ["nmi"],
    crm: ["checkoutchamp"],
  };

  // --- Integration Actions (generic) ---
  const handleProvision = async () => {
    const { provider, credentialId } = provisionForm;
    if (!provider || !credentialId) return;
    const config = PROVIDER_CONFIG[provider];
    if (!config) return;

    setSubmitting(true);
    try {
      const body = { midId, credentialId };
      await makeApiRequest(
        "post",
        config.provisionRoute,
        { id: user.id, accessToken: user.accessToken },
        user.companyId,
        body
      );
      toast({ status: "success", title: `MID provisioned in ${config.label}` });
      setShowProvisionForm(null);
      setProvisionForm({ provider: "", credentialId: "" });
      await fetchIntegrations();
      await fetchData();
    } catch (err) {
      toast({
        status: "destructive",
        title: `${config.label} provisioning failed`,
        description: err.response?.data?.error || err.response?.data?.message || "Unknown error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // --- Link Existing (NMI gateway/processor) ---
  const openLinkForm = (categoryType) => {
    setShowProvisionForm(null);
    setShowLinkForm(categoryType);
    setLinkForm({ credentialId: "", merchantId: "", processorId: "" });
    setLinkGateways([]);
    setLinkCandidates([]);
    fetchLinkCredentials();
  };

  const closeLinkForm = () => {
    setShowLinkForm(null);
    setLinkForm({ credentialId: "", merchantId: "", processorId: "" });
    setLinkGateways([]);
    setLinkCandidates([]);
  };

  const fetchLinkCredentials = async () => {
    try {
      const res = await makeApiRequest(
        "get",
        `${apiRoutes.getCredentials}?provider=nmi&credentialType=api`,
        { id: user.id, accessToken: user.accessToken },
        user.companyId
      );
      setLinkCredentials(res.data?.data || []);
    } catch {
      setLinkCredentials([]);
    }
  };

  const fetchLinkGateways = async (credentialId) => {
    setLinkGatewaysLoading(true);
    try {
      const res = await makeApiRequest(
        "get",
        apiRoutes.nmiGateways(credentialId),
        { id: user.id, accessToken: user.accessToken },
        user.companyId
      );
      setLinkGateways(res.data?.data || []);
    } catch (err) {
      toast({ status: "destructive", title: "Failed to load NMI gateways", description: err.response?.data?.error || "Unknown error" });
      setLinkGateways([]);
    } finally {
      setLinkGatewaysLoading(false);
    }
  };

  const fetchLinkCandidates = async (credentialId, merchantId) => {
    setLinkCandidatesLoading(true);
    try {
      const res = await makeApiRequest(
        "get",
        apiRoutes.nmiLinkCandidates(credentialId, merchantId),
        { id: user.id, accessToken: user.accessToken },
        user.companyId
      );
      const cands = res.data?.data || [];
      setLinkCandidates(cands);
      // Pre-select only when there's a single unambiguous candidate.
      const selectable = cands.filter((c) => !c.alreadyLinked);
      if (selectable.length === 1) {
        setLinkForm((f) => ({ ...f, processorId: selectable[0].processorId }));
      }
    } catch (err) {
      toast({ status: "destructive", title: "Failed to load processors", description: err.response?.data?.error || "Unknown error" });
      setLinkCandidates([]);
    } finally {
      setLinkCandidatesLoading(false);
    }
  };

  const handleLink = async () => {
    const { credentialId, merchantId, processorId } = linkForm;
    if (!credentialId || !merchantId || !processorId) return;
    setLinkSubmitting(true);
    try {
      await makeApiRequest(
        "post",
        apiRoutes.nmiLink,
        { id: user.id, accessToken: user.accessToken },
        user.companyId,
        { midId, credentialId, merchantId, processorId }
      );
      toast({ status: "success", title: "NMI gateway/processor linked" });
      closeLinkForm();
      await fetchIntegrations();
      await fetchData();
    } catch (err) {
      toast({ status: "destructive", title: "Link failed", description: err.response?.data?.error || err.response?.data?.message || "Unknown error" });
    } finally {
      setLinkSubmitting(false);
    }
  };

  // --- Link Existing: Disputifier (SOUND-458) ---
  const openDispLink = () => {
    setShowDispLink(true);
    setDispSearch(""); setDispMerchants([]); setDispMerchant(null);
    setDispCandidates([]); setDispMidId("");
  };
  const closeDispLink = () => {
    setShowDispLink(false); setDispMerchant(null); setDispCandidates([]); setDispMidId("");
  };
  const searchDispMerchants = async (term) => {
    setDispMerchantsLoading(true);
    try {
      const res = await makeApiRequest(
        "get", apiRoutes.disputifierLinkMerchants(term),
        { id: user.id, accessToken: user.accessToken }, user.companyId
      );
      setDispMerchants(res.data?.data || []);
    } catch (err) {
      toast({ status: "destructive", title: "Failed to load Disputifier merchants", description: err.response?.data?.error || "Unknown error" });
      setDispMerchants([]);
    } finally {
      setDispMerchantsLoading(false);
    }
  };
  const selectDispMerchant = async (m) => {
    setDispMerchant(m); setDispCandidates([]); setDispMidId("");
    setDispCandidatesLoading(true);
    try {
      const res = await makeApiRequest(
        "get", apiRoutes.disputifierLinkCandidates(m.merchantId),
        { id: user.id, accessToken: user.accessToken }, user.companyId
      );
      const cands = res.data?.data || [];
      setDispCandidates(cands);
      const selectable = cands.filter((c) => !c.alreadyLinked);
      if (selectable.length === 1) setDispMidId(selectable[0].disputifierMidId);
    } catch (err) {
      toast({ status: "destructive", title: "Failed to load Disputifier MIDs", description: err.response?.data?.error || "Unknown error" });
      setDispCandidates([]);
    } finally {
      setDispCandidatesLoading(false);
    }
  };
  const handleDispLink = async () => {
    if (!dispMerchant || !dispMidId) return;
    setDispLinkSubmitting(true);
    try {
      await makeApiRequest(
        "post", apiRoutes.disputifierLink,
        { id: user.id, accessToken: user.accessToken }, user.companyId,
        { midId, merchantId: dispMerchant.merchantId, disputifierMidId: dispMidId }
      );
      toast({ status: "success", title: "Disputifier MID linked" });
      closeDispLink();
      await fetchDisputifier();
      await fetchIntegrations();
    } catch (err) {
      toast({ status: "destructive", title: "Link failed", description: err.response?.data?.error || "Unknown error" });
    } finally {
      setDispLinkSubmitting(false);
    }
  };

  // --- Link Existing: CRM (CC/PHX) from the risk /unresolved worklist (SOUND-458) ---
  const openCrmLink = () => {
    setShowCrmLink(true); setCrmSearch(""); setCrmSelected(null);
    fetchCrmCandidates();
  };
  const closeCrmLink = () => { setShowCrmLink(false); setCrmSelected(null); };
  const fetchCrmCandidates = async () => {
    setCrmCandidatesLoading(true);
    try {
      const res = await makeApiRequest(
        "get", apiRoutes.riskUnresolved,
        { id: user.id, accessToken: user.accessToken }, user.companyId
      );
      const rows = (res.data?.data || []).filter((r) => r.provider === "cc" || r.provider === "phx");
      setCrmCandidates(rows);
    } catch (err) {
      toast({ status: "destructive", title: "Failed to load linking worklist", description: err.response?.data?.error || "Unknown error" });
      setCrmCandidates([]);
    } finally {
      setCrmCandidatesLoading(false);
    }
  };
  const handleCrmLink = async () => {
    if (!crmSelected) return;
    const { provider, native_key } = crmSelected;
    // native_key = "<slug>-<merchant_id>"; merchant_id is the tail → split on the LAST hyphen
    const idx = (native_key || "").lastIndexOf("-");
    if (idx < 1) {
      toast({ status: "destructive", title: "Unrecognized worklist key", description: native_key });
      return;
    }
    const slug = native_key.slice(0, idx);
    const externalId = native_key.slice(idx + 1);
    const providerName = provider === "cc" ? "checkoutchamp" : "phoenix";
    setCrmLinkSubmitting(true);
    try {
      await makeApiRequest(
        "post", apiRoutes.addMidIntegration(midId),
        { id: user.id, accessToken: user.accessToken }, user.companyId,
        {
          type: "crm", provider: providerName,
          externalMerchantId: slug, externalId,
          status: "active", label: `${slug} #${externalId}`,
          metadata: { source: "worklist-link" },
        }
      );
      toast({ status: "success", title: "CRM MID linked" });
      closeCrmLink();
      await fetchIntegrations();
    } catch (err) {
      toast({ status: "destructive", title: "Link failed", description: err.response?.data?.error || "Unknown error" });
    } finally {
      setCrmLinkSubmitting(false);
    }
  };

  const handleSyncIntegration = async (integration) => {
    const config = PROVIDER_CONFIG[integration.provider];
    if (!config) return;

    setProvisionSyncing(integration._id);
    try {
      await makeApiRequest(
        "post",
        config.syncRoute(midId),
        { id: user.id, accessToken: user.accessToken },
        user.companyId,
        { credentialId: integration.credentials?.credentialId }
      );
      toast({ status: "success", title: `${config.label} data synced` });
      await fetchIntegrations();
    } catch (err) {
      toast({
        status: "destructive",
        title: `${config.label} sync failed`,
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setProvisionSyncing(null);
    }
  };

  // Unlink = CRM-only. DELETE /mid-integrations/:id soft-deactivates our link
  // record (status -> inactive) and touches nothing at the provider, so a
  // mislink is cheap to undo — the tile drops out of the category (which filters
  // inactive) and the Link Existing form comes back.
  //
  // Deliberately NOT surfaced here: the provider deactivate routes
  // (POST /nmi/deactivate/:midId closes the gateway at NMI,
  // POST /checkoutchamp/deactivate/:midId disables the biller in CC). Those stay
  // live on the API for real force-close work, but they are not an unlink and
  // ops was reading the old button as one.
  const handleUnlinkIntegration = (integration) => {
    const label = PROVIDER_CONFIG[integration.provider]?.label || integration.provider;
    const which = integration.externalId ? ` ${integration.externalId}` : "";

    setConfirmDialog({
      open: true,
      title: `Unlink ${label} Integration`,
      description: `Remove the link between this MID and ${label}${which}? This clears the link in the CRM only — nothing changes at ${label}, the gateway stays open, and you can re-link at any time.`,
      confirmLabel: "Unlink",
      onConfirm: async () => {
        closeConfirmDialog();
        setSubmitting(true);
        try {
          await makeApiRequest(
            "delete",
            apiRoutes.unlinkMidIntegration(integration._id),
            { id: user.id, accessToken: user.accessToken },
            user.companyId
          );
          toast({ status: "success", title: `${label} integration unlinked` });
          await fetchIntegrations();
          await fetchData();
        } catch (err) {
          toast({
            status: "destructive",
            title: "Unlink failed",
            description: err.response?.data?.error || "Unknown error",
          });
        } finally {
          setSubmitting(false);
        }
      },
    });
  };

  // --- Category-driven integration section renderer ---
  const renderIntegrationTiles = (categoryIntegrations) =>
    categoryIntegrations.map((intg) => {
      const config = PROVIDER_CONFIG[intg.provider];
      const isSyncing = provisionSyncing === intg._id;
      return (
        <div key={intg._id} className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className={`rounded-lg border p-4 ${
              intg.status === "active" ? "border-success/40 bg-success/5" : "border-border bg-muted/30"
            }`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold uppercase">Status</span>
                {intg.status === "active" ? (
                  <CheckCircle className="h-4 w-4 text-success" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                intg.status === "active" ? "bg-success/20 text-success" : "bg-muted text-muted-foreground"
              }`}>
                {intg.status === "active" ? "Active" : intg.status}
              </span>
            </div>

            <div className="rounded-lg border p-4 border-border bg-muted/30">
              <span className="text-sm font-semibold uppercase block mb-2">External ID</span>
              <span className="text-sm font-mono text-foreground">{intg.externalId || "\u2014"}</span>
            </div>

            <div className="rounded-lg border p-4 border-border bg-muted/30">
              <span className="text-sm font-semibold uppercase block mb-2">Provider</span>
              <span className="text-sm text-foreground">{config?.label || intg.provider}</span>
            </div>

            <div className="rounded-lg border p-4 border-border bg-muted/30">
              <span className="text-sm font-semibold uppercase block mb-2">Provisioned</span>
              <span className="text-sm text-foreground">
                {intg.createdAt ? new Date(intg.createdAt).toLocaleDateString() : "\u2014"}
              </span>
            </div>
          </div>
          {intg.notes && (
            <p className="text-xs text-muted-foreground">{intg.notes}</p>
          )}
          {isAdmin && (
            <div className="flex items-center gap-2">
              {config?.syncRoute && (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => handleSyncIntegration(intg)}
                  disabled={isSyncing}
                >
                  <RefreshCw className={`h-4 w-4 ${isSyncing ? "animate-spin" : ""}`} />
                  {isSyncing ? "Syncing..." : "Sync"}
                </button>
              )}
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => handleUnlinkIntegration(intg)}
                disabled={submitting}
                title="Remove this link in the CRM — nothing changes at the provider"
              >
                <Unlink className="h-4 w-4" />
                Unlink
              </button>
            </div>
          )}
        </div>
      );
    });

  const renderProvisionForm = (categoryType) => {
    const providers = CATEGORY_PROVIDERS[categoryType] || [];
    return (
      <div className="border border-border rounded-lg p-4 space-y-4">
        <h3 className="text-sm font-medium">Provision Integration</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium text-foreground mb-1 block">Provider</label>
            <select
              className="select select-bordered w-full"
              value={provisionForm.provider}
              onChange={(e) => {
                const p = e.target.value;
                setProvisionForm({ provider: p, credentialId: "", gatewayId: "" });
                if (p) fetchProvisionCredentials(p);
              }}
            >
              <option value="">Select provider...</option>
              {providers.map((p) => (
                <option key={p} value={p}>
                  {PROVIDER_CONFIG[p]?.label || p}
                </option>
              ))}
            </select>
          </div>
          {provisionForm.provider && (
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Credential</label>
              <select
                className="select select-bordered w-full"
                value={provisionForm.credentialId}
                onChange={(e) => setProvisionForm({ ...provisionForm, credentialId: e.target.value })}
              >
                <option value="">Select credential...</option>
                {provisionCredentials.map((cred) => (
                  <option key={cred._id} value={cred._id}>
                    {cred.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-foreground mb-1 block">MID Details</label>
            <p className="text-sm text-muted-foreground">
              {mid?.mid} — {mid?.descriptor}
              {mid?.mcc && ` — MCC: ${mid.mcc}`}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            className="btn btn-primary btn-sm"
            onClick={handleProvision}
            disabled={submitting || !provisionForm.credentialId}
          >
            <Save className="h-4 w-4" />
            {submitting ? "Provisioning..." : "Provision"}
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setShowProvisionForm(null);
              setProvisionForm({ provider: "", credentialId: "" });
            }}
          >
            <X className="h-4 w-4" />
            Cancel
          </button>
        </div>
      </div>
    );
  };

  const renderLinkForm = () => {
    const selected = linkCandidates.find((c) => c.processorId === linkForm.processorId);
    return (
      <div className="border border-border rounded-lg p-4 space-y-4">
        <h3 className="text-sm font-medium flex items-center gap-2">
          <Link2 className="h-4 w-4 text-brand" />
          Link Existing NMI Gateway / Processor
        </h3>
        <p className="text-xs text-muted-foreground">
          Pick the NMI instance, its gateway, then the specific active card processor (MID) to attach to{" "}
          <span className="font-medium text-foreground">{mid?.descriptor || mid?.mid}</span>.
          {mid?.processorName ? ` CRM processor tag: ${mid.processorName}.` : ""}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-sm font-medium text-foreground mb-1 block">NMI Instance</label>
            <select
              className="select select-bordered w-full"
              value={linkForm.credentialId}
              onChange={(e) => {
                const cid = e.target.value;
                setLinkForm({ credentialId: cid, merchantId: "", processorId: "" });
                setLinkGateways([]);
                setLinkCandidates([]);
                if (cid) fetchLinkGateways(cid);
              }}
            >
              <option value="">Select instance...</option>
              {linkCredentials.map((c) => (
                <option key={c._id} value={c._id}>{c.label}</option>
              ))}
            </select>
          </div>
          {linkForm.credentialId && (
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Gateway</label>
              <select
                className="select select-bordered w-full"
                value={linkForm.merchantId}
                disabled={linkGatewaysLoading}
                onChange={(e) => {
                  const gw = e.target.value;
                  setLinkForm({ ...linkForm, merchantId: gw, processorId: "" });
                  setLinkCandidates([]);
                  if (gw) fetchLinkCandidates(linkForm.credentialId, gw);
                }}
              >
                <option value="">{linkGatewaysLoading ? "Loading gateways..." : "Select gateway..."}</option>
                {linkGateways.map((g) => (
                  <option key={g.id} value={g.id}>
                    {(g.company || "(no name)")} — {g.id}{g.status && g.status !== "active" ? ` (${g.status})` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
          {linkForm.merchantId && (
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Processor (MID)</label>
              <select
                className="select select-bordered w-full"
                value={linkForm.processorId}
                disabled={linkCandidatesLoading}
                onChange={(e) => setLinkForm({ ...linkForm, processorId: e.target.value })}
              >
                <option value="">
                  {linkCandidatesLoading
                    ? "Loading processors..."
                    : linkCandidates.length
                    ? "Select processor..."
                    : "No active card processors"}
                </option>
                {linkCandidates.map((p) => (
                  <option key={p.processorId} value={p.processorId} disabled={p.alreadyLinked}>
                    {(p.processorName || p.serviceId || "card")} — {p.processorId}
                    {p.alreadyLinked ? " (already linked)" : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        {selected && (
          <p className="text-xs text-muted-foreground">
            Selected: <span className="font-medium text-foreground">{selected.processorName || selected.serviceId}</span>
            {" · "}processor {selected.processorId}
            {" · "}acquirer MID {selected.backendMid || "—"}
          </p>
        )}
        <div className="flex gap-2">
          <button className="btn btn-primary btn-sm" onClick={handleLink} disabled={linkSubmitting || !linkForm.processorId}>
            <Link2 className="h-4 w-4" />
            {linkSubmitting ? "Linking..." : "Link"}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={closeLinkForm}>
            <X className="h-4 w-4" />
            Cancel
          </button>
        </div>
      </div>
    );
  };

  const renderDispLinkForm = () => (
    <div className="border border-border rounded-lg p-4 space-y-4">
      <h3 className="text-sm font-medium flex items-center gap-2">
        <Link2 className="h-4 w-4 text-brand" />
        Link Existing Disputifier MID
      </h3>
      <p className="text-xs text-muted-foreground">
        Find the Disputifier merchant, then pick the specific MID to attach to{" "}
        <span className="font-medium text-foreground">{mid?.descriptor || mid?.mid}</span>.
      </p>
      <div className="flex gap-2">
        <input
          className="input input-bordered w-full"
          placeholder="Search Disputifier merchants by name or ID..."
          value={dispSearch}
          onChange={(e) => setDispSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") searchDispMerchants(dispSearch); }}
        />
        <button className="btn btn-secondary btn-sm" onClick={() => searchDispMerchants(dispSearch)} disabled={dispMerchantsLoading}>
          {dispMerchantsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
        </button>
      </div>
      {!dispMerchant && dispMerchants.length > 0 && (
        <div className="max-h-48 overflow-y-auto space-y-1">
          {dispMerchants.map((m) => (
            <button
              key={m.merchantId}
              className="w-full text-left px-3 py-2 rounded-lg border border-border hover:bg-muted/50 text-sm"
              onClick={() => selectDispMerchant(m)}
            >
              <span className="font-medium text-foreground">{m.merchantName || "(no name)"}</span>
              <span className="text-muted-foreground"> · {m.merchantId} · {m.midCount} MID{m.midCount === 1 ? "" : "s"}</span>
            </button>
          ))}
        </div>
      )}
      {dispMerchant && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Merchant: <span className="font-medium text-foreground">{dispMerchant.merchantName || dispMerchant.merchantId}</span>
            {" · "}
            <button className="text-brand underline" onClick={() => { setDispMerchant(null); setDispCandidates([]); setDispMidId(""); }}>change</button>
          </p>
          <label className="text-sm font-medium text-foreground mb-1 block">MID</label>
          <select
            className="select select-bordered w-full"
            value={dispMidId}
            disabled={dispCandidatesLoading}
            onChange={(e) => setDispMidId(e.target.value)}
          >
            <option value="">
              {dispCandidatesLoading ? "Loading MIDs..." : dispCandidates.length ? "Select MID..." : "No MIDs under this merchant"}
            </option>
            {dispCandidates.map((c) => (
              <option key={c.disputifierMidId} value={c.disputifierMidId} disabled={c.alreadyLinked}>
                {c.midName || c.disputifierMidId}{c.alreadyLinked ? " (already linked)" : ""}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="flex gap-2">
        <button className="btn btn-primary btn-sm" onClick={handleDispLink} disabled={dispLinkSubmitting || !dispMidId}>
          <Link2 className="h-4 w-4" />
          {dispLinkSubmitting ? "Linking..." : "Link"}
        </button>
        <button className="btn btn-ghost btn-sm" onClick={closeDispLink}>
          <X className="h-4 w-4" />
          Cancel
        </button>
      </div>
    </div>
  );

  const renderCrmLinkForm = () => {
    const q = crmSearch.trim().toLowerCase();
    const filtered = q ? crmCandidates.filter((c) => (c.native_key || "").toLowerCase().includes(q)) : crmCandidates;
    return (
      <div className="border border-border rounded-lg p-4 space-y-4">
        <h3 className="text-sm font-medium flex items-center gap-2">
          <Link2 className="h-4 w-4 text-brand" />
          Link Existing CRM MID
        </h3>
        <p className="text-xs text-muted-foreground">
          We don't have CheckoutChamp / Phoenix API access, so pick an unlinked MID we've already seen in the risk
          data (the linking worklist) and attribute it to{" "}
          <span className="font-medium text-foreground">{mid?.descriptor || mid?.mid}</span>.
        </p>
        <input
          className="input input-bordered w-full"
          placeholder="Filter by account / merchant_id..."
          value={crmSearch}
          onChange={(e) => setCrmSearch(e.target.value)}
        />
        {crmCandidatesLoading ? (
          <Skeleton.Rect className="h-24 w-full" />
        ) : filtered.length ? (
          <div className="max-h-56 overflow-y-auto space-y-1">
            {filtered.map((c) => {
              const active = crmSelected && crmSelected.sound_mid_id === c.sound_mid_id;
              return (
                <button
                  key={c.sound_mid_id}
                  onClick={() => setCrmSelected(c)}
                  className={`w-full text-left px-3 py-2 rounded-lg border text-sm ${active ? "border-brand bg-brand/5" : "border-border hover:bg-muted/50"}`}
                >
                  <span className="font-mono text-foreground">{c.provider}:{c.native_key}</span>
                  <span className="text-muted-foreground">
                    {" · "}{Number(c.total_settled || 0).toLocaleString()} settled
                    {" · "}{Number(c.total_chargebacks || 0)} CB
                    {" · "}{Number(c.tc40_count || 0) + Number(c.ethoca_count || 0) + Number(c.cdrn_count || 0) + Number(c.rdr_count || 0)} alerts
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-4">No unlinked CC/PHX candidates in the worklist.</p>
        )}
        <div className="flex gap-2">
          <button className="btn btn-primary btn-sm" onClick={handleCrmLink} disabled={crmLinkSubmitting || !crmSelected}>
            <Link2 className="h-4 w-4" />
            {crmLinkSubmitting ? "Linking..." : "Link"}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={closeCrmLink}>
            <X className="h-4 w-4" />
            Cancel
          </button>
        </div>
      </div>
    );
  };

  // `Icon` is still needed — not for the header, which no longer carries one,
  // but for the empty-state illustration below, where it is doing real work.
  const renderIntegrationCategory = (categoryType, title, Icon) => {
    const categoryIntegrations = integrations.filter(
      (i) => i.type === categoryType && i.status !== "inactive"
    );

    return (
      <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-foreground">
            {title}
          </h2>
        </div>

        {integrationsLoading ? (
          <Skeleton.Rect className="h-24 w-full" />
        ) : categoryIntegrations.length > 0 ? (
          <div className="space-y-6">
            {renderIntegrationTiles(categoryIntegrations)}
          </div>
        ) : isAdmin ? (
          <>
            {categoryType === "gateway" && showLinkForm === "gateway" ? (
              renderLinkForm()
            ) : categoryType === "crm" && showCrmLink ? (
              renderCrmLinkForm()
            ) : (
              <div className="text-center py-6">
                <Icon className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground mb-3">
                  No {title.toLowerCase()} linked for this MID
                </p>
                <div className="flex gap-2 justify-center">
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => (categoryType === "gateway" ? openLinkForm("gateway") : openCrmLink())}
                  >
                    <Link2 className="h-4 w-4" />
                    Link Existing
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            No {title.toLowerCase()} configured
          </p>
        )}
      </section>
    );
  };

  // --- Credential Handlers ---

  const resetCredentialForm = () => {
    setCredentialForm({
      provider: "",
      label: "",
      visibility: "internal",
      portal: { url: "", username: "", has2fa: false, notes: "" },
      secrets: [{ key: "", value: "" }],
    });
    setEditingCredential(null);
  };

  // SOUND-709 surface 3 — create uses the SHARED modal. This surface already
  // knows the client AND the MID, so it asks for neither; the old form asked
  // for a free-text provider and arbitrary "field name / value" secret pairs,
  // which is the derived-key era it predates.
  //
  // EDIT still uses the legacy modal below. Splitting create out first is
  // deliberate: create is where the vault write and the MID edge happen, and
  // where a wrong provider string becomes a permanent record.
  const openCreateCredentialModal = () => setShowCreateCredential(true);

  const openEditCredentialModal = (cred) => {
    setEditingCredential(cred);
    const secretEntries = Object.entries(cred.credentials || {}).map(([refKey]) => {
      const fieldName = refKey.endsWith("SecretName") ? refKey.slice(0, -"SecretName".length) : refKey;
      return { key: fieldName, value: "", changed: false };
    });
    setCredentialForm({
      provider: cred.provider,
      label: cred.label,
      visibility: cred.visibility || "internal",
      portal: {
        url: cred.portal?.url || "",
        username: cred.portal?.username || "",
        has2fa: !!cred.portal?.has2fa,
        notes: cred.portal?.notes || "",
      },
      secrets: secretEntries.length > 0 ? secretEntries : [{ key: "", value: "" }],
    });
    setShowCredentialModal(true);
  };

  const handleSaveCredential = async () => {
    const { provider, label, visibility, portal, secrets } = credentialForm;
    if (!provider || !label) {
      toast({ status: "destructive", title: "Provider and label are required" });
      return;
    }

    setCredentialSubmitting(true);
    try {
      if (editingCredential) {
        // Update
        const body = {};
        if (provider !== editingCredential.provider) body.provider = provider;
        if (label !== editingCredential.label) body.label = label;
        if (visibility !== (editingCredential.visibility || "internal")) body.visibility = visibility;

        // Portal changes
        const oldPortal = editingCredential.portal || {};
        const portalChanges = {};
        if (portal.url !== (oldPortal.url || "")) portalChanges.url = portal.url || null;
        if (portal.username !== (oldPortal.username || "")) portalChanges.username = portal.username || null;
        if (portal.has2fa !== !!oldPortal.has2fa) portalChanges.has2fa = portal.has2fa;
        if (portal.notes !== (oldPortal.notes || "")) portalChanges.notes = portal.notes || null;
        if (Object.keys(portalChanges).length > 0) body.portal = portalChanges;

        // Secret changes
        const changedSecrets = {};
        for (const { key, value, changed } of secrets) {
          if (changed && value.trim()) changedSecrets[key] = value.trim();
        }
        if (Object.keys(changedSecrets).length > 0) body.secrets = changedSecrets;

        if (Object.keys(body).length === 0) {
          toast({ status: "info", title: "No changes to save" });
          setCredentialSubmitting(false);
          return;
        }

        await makeApiRequest("put", apiRoutes.updateCredential(editingCredential._id), { id: user.id, accessToken: user.accessToken }, user.companyId, body);
        toast({ status: "success", title: "Credential updated" });
      } else {
        // Create
        const secretsObj = {};
        for (const { key, value } of secrets) {
          if (key.trim() && value.trim()) secretsObj[key.trim()] = value.trim();
        }
        const hasSecrets = Object.keys(secretsObj).length > 0;
        const hasPortal = portal.url || portal.username || portal.has2fa || portal.notes;
        if (!hasSecrets && !hasPortal) {
          toast({ status: "destructive", title: "At least one of secrets or portal info is required" });
          setCredentialSubmitting(false);
          return;
        }

        const body = {
          companyId: mid.companyId,
          provider,
          label,
          midId: mid.midId,
          visibility,
        };
        if (hasSecrets) body.secrets = secretsObj;
        if (hasPortal) body.portal = { url: portal.url || null, username: portal.username || null, has2fa: !!portal.has2fa, notes: portal.notes || null };

        await makeApiRequest("post", apiRoutes.createCredential, { id: user.id, accessToken: user.accessToken }, user.companyId, body);
        toast({ status: "success", title: "Credential created" });
      }

      setShowCredentialModal(false);
      resetCredentialForm();
      fetchMidCredentials();
    } catch (err) {
      toast({ status: "destructive", title: "Failed to save credential", description: err.response?.data?.error || "Unknown error" });
    } finally {
      setCredentialSubmitting(false);
    }
  };

  const handleDeactivateCredential = (cred) => {
    setConfirmDialog({
      open: true,
      title: "Deactivate Credential",
      description: `Deactivate ${cred.provider} credential "${cred.label}"?`,
      confirmLabel: "Deactivate",
      variant: "destructive",
      onConfirm: async () => {
        closeConfirmDialog();
        try {
          await makeApiRequest("delete", apiRoutes.deleteCredential(cred._id), { id: user.id, accessToken: user.accessToken }, user.companyId);
          toast({ status: "success", title: "Credential deactivated" });
          fetchMidCredentials();
        } catch (err) {
          toast({ status: "destructive", title: "Failed to deactivate", description: err.response?.data?.error || "Unknown error" });
        }
      },
    });
  };


  // --- Render ---
  // Counts what the grid actually draws, so the header cannot claim a number
  // the section does not show. `blank` is the useful half — it is the visible
  // signal of what the record is missing, the same reading the registration
  // sections give.
  const midDetailFieldCount = React.useMemo(() => {
    if (!mid) return { total: 0, empty: 0 };
    const values = [
      mid.name,
      mid.mid,
      mid.descriptor,
      mid.mcc,
      mid.caid,
      mid.acquiringBank,
      mid.bin,
      mid.source?.type,
      (mid.currencies || []).join(", "),
      mid.multiCurrency ? "Yes" : "No",
      mid.isUncapped ? true : mid.monthlyVolumeCap,
      mid.chargebackThreshold,
      mid.effectiveDate,
      mid.notes,
      mid.tags?.length ? mid.tags : null,
    ];
    return {
      total: values.length,
      empty: values.filter((v) => v === null || v === undefined || v === "").length,
    };
  }, [mid]);

  if (loading) {
    return (
      <div className="p-6">
        <Skeleton.Rect className="h-8 w-48 mb-4" />
        <Skeleton.Rect className="h-5 w-full mb-2" />
        <Skeleton.Rect className="h-5 w-2/3 mb-2" />
      </div>
    );
  }

  if (!mid) {
    return (
      <div className="p-6 text-center">
        <CreditCard className="mx-auto h-12 w-12 text-muted-foreground" />
        <h3 className="mt-2 text-sm font-medium text-foreground">
          MID not found
        </h3>
      </div>
    );
  }

  const linked = mid._linked || {};
  const availableTransitions = MID_MANAGER_TRANSITIONS[mid.status] || [];

  return (
    <div>
      <PageHeader
        title={mid.name || `MID ${mid.mid}`}
        subtitle={mid.name ? `${mid.mid} · ${mid.descriptor || ""}` : mid.descriptor}
        badge={
          mid.isSoundOwned !== false ? (
            <span
              title="Sound-owned MID"
              className="inline-flex h-6 w-6 shrink-0 overflow-hidden rounded-full ring-1 ring-border"
            >
              <img src={logoBSquare} alt="Sound-owned" className="h-full w-full object-cover" />
            </span>
          ) : null
        }
        actions={
          <>
            <button className="btn btn-ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            {isAdmin && (
              <button className="btn btn-ghost" onClick={startEditing}>
                <Pencil className="h-4 w-4" />
                Edit
              </button>
            )}
            <button
              className="btn btn-primary"
              onClick={() => navigate("/mids")}
            >
              Portfolio
            </button>
          </>
        }
      />

      {/* Matches the registration tab, /mid-applications/:id and the ticket
          detail page: a fixed 320px rail, main column takes the remainder. */}
      <div className="container grid grid-cols-1 items-start gap-6 py-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-6">
        {/* MID Details */}
        {/* SOUND-649: brought onto the record language established by the
            registration tab — a plain semibold title with a field count, no
            brand-tinted icon tile. The rail already read this way; the main
            column was still the older treatment, so one page spoke two dialects.

            COMPANY and PROCESSOR are gone from this grid. Both are
            RELATIONSHIPS, and they render in the Linked panel where they can be
            opened. Company was showing a raw UUID here — an identifier printed
            at a human — and Processor was a second copy of a pinned row. */}
        <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-base font-semibold text-foreground">MID Details</h2>
            <span className="text-xs text-muted-foreground">
              {midDetailFieldCount.total} fields
              {midDetailFieldCount.empty > 0 && (
                <span className="text-muted-foreground/60">
                  {" "}· {midDetailFieldCount.empty} blank
                </span>
              )}
            </span>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            <InfoField label="Name" value={mid.name} />
            <InfoField label="MID" value={mid.mid} />
            <InfoField label="Descriptor" value={mid.descriptor} />
            <InfoField label="MCC" value={mid.mcc} />
            <InfoField label="CAID" value={mid.caid} />
            <InfoField label="Acquiring Bank" value={mid.acquiringBank} />
            <InfoField label="BIN" value={mid.bin} />
            <InfoField
              label="Source"
              value={<SourceBadge source={mid.source} />}
            />
            <InfoField
              label="Currencies"
              value={(mid.currencies || []).join(", ") || "USD"}
            />
            <InfoField
              label="Multi-Currency"
              value={mid.multiCurrency ? "Yes" : "No"}
            />
            <InfoField
              label="Monthly Volume Cap"
              value={formatVolumeCap(mid)}
            />
            <InfoField
              label="Chargeback Threshold"
              value={mid.chargebackThreshold != null ? `${mid.chargebackThreshold}%` : null}
            />
            <InfoField
              label="Effective Date"
              value={mid.effectiveDate ? new Date(mid.effectiveDate).toLocaleDateString() : null}
            />
            <InfoField label="Notes" value={mid.notes} />
            <InfoField
              label="Tags"
              value={
                mid.tags?.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {mid.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-brand/10 text-brand"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : null
              }
            />
          </div>
        </section>

        {/* Reserve */}
        {mid.reserve && mid.reserve.type !== "none" && (
          <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
            <h2 className="text-base font-semibold text-foreground">
              Reserve
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoField label="Type" value={mid.reserve.type} />
              <InfoField
                label="Percentage"
                value={mid.reserve.percentage != null ? `${mid.reserve.percentage}%` : null}
              />
              <InfoField
                label="Hold Period"
                value={mid.reserve.holdPeriod != null ? `${mid.reserve.holdPeriod} days` : null}
              />
              <InfoField
                label="Cap"
                value={mid.reserve.cap != null ? `$${mid.reserve.cap.toLocaleString()}` : null}
              />
              <InfoField label="Notes" value={mid.reserve.notes} />
            </div>
          </section>
        )}

        {/* Portals & Credentials — SOUND-709.
            Was a hand-rolled <table> from the first iteration with its own
            columns, its own styling and a "Secrets" column that showed a dash
            for everything. It is the same kind of list as /credentials and the
            client tab, so it uses the same components: one look, one set of
            behaviours, and a credential row means the same thing wherever a
            person meets it.

            Columns are the MID-level subset — provider, label, username, secret
            storage — because CORP, DBA, PROCESSOR and MID NUMBER are all fixed
            on this page and repeating them in every row says nothing. */}
        <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-foreground">
              Portals &amp; Credentials
            </h2>
            {isAdmin && (
              <button className="btn btn-primary btn-sm" onClick={openCreateCredentialModal}>
                <Plus className="h-4 w-4" /> Add Credential
              </button>
            )}
          </div>

          <RecordTable
            columns={midCredentialColumns}
            rows={midCredentials}
            rowKey={(c) => c.credentialId || c._id}
            onRowClick={(c) => c.credentialId && navigate(`/credentials/${c.credentialId}`)}
            rowClassName={(c) => (c.credentialId ? "" : "opacity-60")}
            loading={midCredentialsLoading}
            skeletonRows={3}
            empty={{
              icon: KeyRound,
              heading: "No credentials for this MID",
              description: "Add one, or attach an existing credential from its record.",
              errorText: "Failed to load credentials.",
            }}
          />
        </section>

        {/* Gateway Integrations (category-driven) */}
        {renderIntegrationCategory("gateway", "Gateway Integrations", Link2)}

        {/* CRM Integrations (category-driven) */}
        {renderIntegrationCategory("crm", "CRM Integrations", Layers)}

        {/* Chargeback Prevention */}
        <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-foreground">
              Chargeback Prevention
            </h2>
            {disputifierData && disputifierData.localPrograms && isAdmin && (
              <div className="flex items-center gap-2">
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={handleSyncDisputifier}
                  disabled={syncing}
                >
                  <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
                  {syncing ? "Syncing..." : "Sync Status"}
                </button>
                <button
                  className="btn btn-ghost btn-sm text-destructive"
                  onClick={handleCloseDisputifier}
                  disabled={submitting}
                >
                  <XCircle className="h-4 w-4" />
                  Close
                </button>
              </div>
            )}
          </div>

          {disputifierLoading ? (
            <Skeleton.Rect className="h-24 w-full" />
          ) : disputifierData ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {["rdr", "cdrn", "ethoca", "tc40"].map((program) => {
                  const local = disputifierData.localPrograms?.[program];
                  const live = disputifierData.enrollments?.[program];
                  const isTc40 = program === "tc40";
                  const midClosed = disputifierData.status === "closed";

                  if (isTc40) {
                    const isActivated = local?.enrolled && !midClosed;
                    return (
                      <div
                        key={program}
                        className={`rounded-lg border p-4 ${
                          isActivated ? "border-success/40 bg-success/5" : "border-border bg-muted/30"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold uppercase">{program}</span>
                          <div className="relative group">
                            <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                            <div className="absolute right-0 bottom-full mb-2 w-48 p-2 bg-popover text-popover-foreground text-xs rounded-lg shadow-lg border border-border opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                              TC40 enrollment status is not tracked by Disputifier. This shows whether TC40 was activated at enrollment time.
                            </div>
                          </div>
                        </div>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            isActivated ? "bg-success/20 text-success" : "bg-muted text-muted-foreground"
                          }`}
                        >
                          {isActivated ? "Activated" : "Inactive"}
                        </span>
                      </div>
                    );
                  }

                  const status = live?.Status || local?.status || "not enrolled";
                  const isLive = status.toLowerCase() === "live";
                  const isInProgress = status.toLowerCase() === "in progress" || status === "submitted";
                  const isEnrolled = local?.enrolled;

                  return (
                    <div
                      key={program}
                      className={`rounded-lg border p-4 ${
                        isLive
                          ? "border-success/40 bg-success/5"
                          : isInProgress
                          ? "border-warning/40 bg-warning/5"
                          : "border-border bg-muted/30"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold uppercase">{program}</span>
                        {isLive ? (
                          <CheckCircle className="h-4 w-4 text-success" />
                        ) : isInProgress ? (
                          <AlertCircle className="h-4 w-4 text-warning" />
                        ) : isEnrolled ? (
                          <Clock className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <XCircle className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          isLive
                            ? "bg-success/20 text-success"
                            : isInProgress
                            ? "bg-warning/20 text-warning"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {isLive ? "Live" : isInProgress ? "In Progress" : isEnrolled ? "Submitted" : "Not Enrolled"}
                      </span>
                      {(live?.SubmittedDate || local?.submittedDate) && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Submitted: {new Date(live?.SubmittedDate || local?.submittedDate).toLocaleDateString()}
                        </p>
                      )}
                      {live?.LiveDate && (
                        <p className="text-xs text-success mt-1">
                          Live: {new Date(live.LiveDate).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                Disputifier MID ID: {disputifierData.disputifierMidId}
              </div>
            </>
          ) : isAdmin ? (
            <>
              {showDispLink ? (
                renderDispLinkForm()
              ) : !showEnrollForm ? (
                <div className="text-center py-6">
                  <Shield className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground mb-3">
                    No chargeback prevention linked for this MID
                  </p>
                  <button className="btn btn-primary btn-sm" onClick={openDispLink}>
                    <Link2 className="h-4 w-4" />
                    Link Existing
                  </button>
                </div>
              ) : (
                <div className="border border-border rounded-lg p-4 space-y-4">
                  <h3 className="text-sm font-medium">Enroll MID in Disputifier</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium text-foreground mb-1 block">DBA</label>
                      <select
                        className="select select-bordered w-full"
                        value={enrollForm.dbaId}
                        onChange={(e) => setEnrollForm({ ...enrollForm, dbaId: e.target.value })}
                      >
                        <option value="">Select DBA...</option>
                        {dbas.map((dba) => (
                          <option key={dba.dbaId} value={dba.dbaId}>
                            {dba.dbaName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground mb-1 block">Email</label>
                      <input
                        type="email"
                        className="input input-bordered w-full"
                        placeholder="Contact email"
                        value={enrollForm.email}
                        onChange={(e) => setEnrollForm({ ...enrollForm, email: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground mb-1 block">Threshold Amount</label>
                      <input
                        type="number"
                        className="input input-bordered w-full"
                        placeholder="Max refund amount"
                        value={enrollForm.thresholdAmount}
                        onChange={(e) => setEnrollForm({ ...enrollForm, thresholdAmount: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground mb-1 block">MID Details</label>
                      <p className="text-sm text-muted-foreground">
                        {mid?.mid} — {mid?.descriptor}
                        {mid?.bin && ` — BIN: ${mid.bin}`}
                        {mid?.caid && ` — CAID: ${mid.caid}`}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">Programs</label>
                    <div className="flex flex-wrap gap-4">
                      {["rdr", "cdrn", "ethoca", "tc40"].map((p) => (
                        <label key={p} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={enrollForm.programs[p]}
                            onChange={(e) =>
                              setEnrollForm({
                                ...enrollForm,
                                programs: { ...enrollForm.programs, [p]: e.target.checked },
                              })
                            }
                          />
                          {p.toUpperCase()}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleEnrollDisputifier}
                      disabled={submitting || !enrollForm.dbaId}
                    >
                      <Save className="h-4 w-4" />
                      {submitting ? "Enrolling..." : "Enroll"}
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => setShowEnrollForm(false)}
                    >
                      <X className="h-4 w-4" />
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              No chargeback prevention configured
            </p>
          )}
        </section>

        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions */}
          {isAdmin && availableTransitions.length > 0 && (
            <section className="surface p-4">
              <h3 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Settings className="h-3.5 w-3.5" />
                Actions
              </h3>
              <div className="space-y-2">
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="select w-full py-1.5 text-sm"
                >
                  <option value="">Move to&hellip;</option>
                  {availableTransitions.map((s) => (
                    <option key={s} value={s}>
                      {midStatusLabel(s)}
                    </option>
                  ))}
                </select>
                {/* Reason is optional but must survive the move to the rail —
                    dropping it would silently remove the only explanation a MID
                    status change ever carries. */}
                <input
                  type="text"
                  className="input w-full py-1.5 text-sm"
                  placeholder="Reason (optional)"
                  value={statusReason}
                  onChange={(e) => setStatusReason(e.target.value)}
                />
                <button
                  className="btn btn-outline w-full justify-start gap-2"
                  onClick={handleStatusChange}
                  disabled={submitting || !selectedStatus}
                >
                  <Settings className="h-4 w-4" />
                  <span>Update status</span>
                </button>
              </div>
            </section>
          )}

          {/* Status Timeline — mids store statusHistory as
              { from, to, reason, changedBy, timestamp }; buildStatusTimeline
              normalises that against the two other shapes the CRM uses. */}
          <section className="surface p-4">
            <h3 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Clock className="h-3.5 w-3.5" />
              Status Timeline
            </h3>
            <div className="mb-4 flex items-center justify-between gap-2 border-b border-border/60 pb-3">
              <span className="text-xs text-muted-foreground">Current status</span>
              <RecordStatusPill status={mid.status} toneFor={statusToneFrom(MID_STATUS)} />
            </div>
            <RecordTimeline
              entries={buildStatusTimeline(mid.statusHistory, {
                createdAt: mid.createdAt,
                createdLabel: "MID created",
                labelFor: statusLabelFrom(MID_STATUS),
                dotFor: statusDotFrom(MID_STATUS),
              })}
            />
            {mid.updatedAt && (
              <div className="mt-4 border-t border-border/60 pt-3 text-[11px] text-muted-foreground">
                Last updated {new Date(mid.updatedAt).toLocaleDateString()}
              </div>
            )}
          </section>

          {/* SOUND-649: ONE panel. Ownership — the company this MID belongs to
              and its processor — is pinned and cannot be unlinked, because those
              are foreign keys, not associations. Everything the MID merely
              relates to is an edge, listed below and removable.

              SOUND-655: the originating application USED to be pinned here, back
              when it was a `mids.applicationId` column. It is an edge now, so
              `linked.application` is itself ASSEMBLED FROM that edge — pinning it
              rendered the same relationship twice, once as "MID Application" and
              again as "Application". Pinning an association to make it show up is
              exactly what the panel's contract forbids. */}
          {isAdmin && (
            <RecordLinksPanel
              entityType="mid"
              entityId={mid.midId}
              companyId={mid.companyId}
              pinned={[
                linked.registration && {
                  key: "registration",
                  icon: Building2,
                  label:
                    linked.registration.businessInfo?.legalName ||
                    linked.registration.companyId,
                  sublabel: `Registration · ${linked.registration.status}`,
                  href: `/companies/${linked.registration.companyId}`,
                },
                linked.processor && {
                  key: "processor",
                  icon: Cpu,
                  label: linked.processor.name || linked.processor.processorId,
                  sublabel: "Processor",
                },
              ].filter(Boolean)}
            />
          )}

          {/* Linked tickets (reverse lookup — SOUND-497), internal-only */}
          {isAdmin && (
            <EntityTicketsSection entityType="mid" entityId={mid.midId} variant="rail" />
          )}
        </div>
      </div>

      {/* Credential Create/Edit Modal */}
      {showCreateCredential && mid && (
        <CredentialCreateModal
          companyId={mid.companyId}
          midId={mid.midId}
          contextLabel={`Owned by this client and linked to this MID${
            mid.descriptor ? ` — ${mid.descriptor}` : ""
          }${mid.mid ? ` · ${mid.mid}` : ""}.`}
          onClose={() => setShowCreateCredential(false)}
          onCreated={fetchMidCredentials}
        />
      )}

      {/* LEGACY — edit only. Create moved to the shared CredentialCreateModal
          above. Left in place rather than rewritten because edit has a distinct
          shape (diffing against the existing doc) and rewriting it blind is how
          the create flow got deleted in the first place. Tracked for A4's
          remaining scope. */}
      {showCredentialModal && editingCredential && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="surface rounded-xl ring-1 ring-border p-6 w-full max-w-lg shadow-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                Edit Credential
              </h3>
              <button className="btn btn-ghost btn-xs" onClick={() => { setShowCredentialModal(false); resetCredentialForm(); }}>
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-foreground block mb-1">Provider</label>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    placeholder="e.g. Netevia"
                    value={credentialForm.provider}
                    onChange={(e) => setCredentialForm((p) => ({ ...p, provider: e.target.value }))}
                    list="cred-provider-presets"
                  />
                  <datalist id="cred-provider-presets">
                    {mid?.processor && <option value={mid.processor} />}
                    <option value="NMI" />
                    <option value="Kurv" />
                    <option value="Elevate" />
                    <option value="CheckoutChamp" />
                  </datalist>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-1">Label</label>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    placeholder="e.g. Merchant Portal"
                    value={credentialForm.label}
                    onChange={(e) => setCredentialForm((p) => ({ ...p, label: e.target.value }))}
                  />
                </div>
              </div>

              {/* Portal section */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-foreground block">Portal Info</label>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">URL</label>
                  <input
                    type="url"
                    className="input input-bordered w-full"
                    placeholder="https://portal.example.com"
                    value={credentialForm.portal.url}
                    onChange={(e) => setCredentialForm((p) => ({ ...p, portal: { ...p.portal, url: e.target.value } }))}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Username</label>
                    <input
                      type="text"
                      className="input input-bordered w-full"
                      placeholder="username"
                      value={credentialForm.portal.username}
                      onChange={(e) => setCredentialForm((p) => ({ ...p, portal: { ...p.portal, username: e.target.value } }))}
                    />
                  </div>
                  <div className="flex items-end pb-2">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={credentialForm.portal.has2fa}
                        onChange={(e) => setCredentialForm((p) => ({ ...p, portal: { ...p.portal, has2fa: e.target.checked } }))}
                        className="rounded border-border"
                      />
                      <span className="font-medium">2FA Required</span>
                    </label>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Notes</label>
                  <textarea
                    className="input input-bordered w-full"
                    rows={2}
                    placeholder="Optional notes"
                    value={credentialForm.portal.notes}
                    onChange={(e) => setCredentialForm((p) => ({ ...p, portal: { ...p.portal, notes: e.target.value } }))}
                  />
                </div>
              </div>

              {/* Secrets section */}
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  Secret Values <span className="text-xs text-muted-foreground font-normal">(optional)</span>
                </label>
                {editingCredential && (
                  <p className="text-xs text-muted-foreground mb-2">Leave blank to keep current value.</p>
                )}
                <div className="space-y-2">
                  {credentialForm.secrets.map((s, i) => (
                    <div key={i} className="flex items-center gap-2">
                      {editingCredential ? (
                        <span className="text-xs font-medium text-muted-foreground w-28 shrink-0">{s.key}</span>
                      ) : (
                        <input
                          type="text"
                          className="input input-bordered flex-1"
                          placeholder="Field name"
                          value={s.key}
                          onChange={(e) => {
                            const updated = [...credentialForm.secrets];
                            updated[i] = { ...updated[i], key: e.target.value };
                            setCredentialForm((p) => ({ ...p, secrets: updated }));
                          }}
                        />
                      )}
                      <input
                        type="password"
                        className="input input-bordered flex-1"
                        placeholder={editingCredential ? "Enter new value" : "Value"}
                        value={s.value}
                        onChange={(e) => {
                          const updated = [...credentialForm.secrets];
                          updated[i] = { ...updated[i], value: e.target.value, changed: true };
                          setCredentialForm((p) => ({ ...p, secrets: updated }));
                        }}
                      />
                      {!editingCredential && credentialForm.secrets.length > 1 && (
                        <button
                          className="btn btn-ghost btn-xs text-destructive"
                          onClick={() => setCredentialForm((p) => ({ ...p, secrets: p.secrets.filter((_, j) => j !== i) }))}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {!editingCredential && (
                  <button
                    className="btn btn-ghost btn-xs mt-2"
                    onClick={() => setCredentialForm((p) => ({ ...p, secrets: [...p.secrets, { key: "", value: "" }] }))}
                  >
                    <Plus className="h-3.5 w-3.5" /> Add Field
                  </button>
                )}
              </div>

              {/* Visibility */}
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={credentialForm.visibility === "client"}
                  onChange={(e) => setCredentialForm((p) => ({ ...p, visibility: e.target.checked ? "client" : "internal" }))}
                  className="rounded border-border"
                />
                <span className="font-medium">Visible to client</span>
              </label>

              <div className="flex gap-2 pt-2">
                <button className="btn btn-ghost flex-1" onClick={() => { setShowCredentialModal(false); resetCredentialForm(); }} disabled={credentialSubmitting}>
                  Cancel
                </button>
                <button className="btn btn-primary flex-1" onClick={handleSaveCredential} disabled={credentialSubmitting}>
                  {credentialSubmitting ? "Saving..." : editingCredential ? "Save Changes" : "Create Credential"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog {...confirmDialog} onCancel={closeConfirmDialog} />

      {/* Edit MID Modal */}
      {isAdmin && editing && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm p-4 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-mid-title"
          onKeyDown={(e) => e.key === "Escape" && setEditing(false)}
        >
          <div className="bg-card text-card-foreground border border-border rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h2 id="edit-mid-title" className="text-xl font-semibold tracking-tight">
                Edit MID
              </h2>
              <button
                onClick={() => setEditing(false)}
                aria-label="Close"
                className="btn btn-ghost h-9 w-9 p-0"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 overflow-y-auto max-h-[calc(90vh-8rem)] space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-foreground mb-1 block">
                    Name
                  </label>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    placeholder="e.g. Acme Health — Stripe MID"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Descriptor</label>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    value={editForm.descriptor}
                    onChange={(e) => setEditForm({ ...editForm, descriptor: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">MCC</label>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    value={editForm.mcc}
                    onChange={(e) => setEditForm({ ...editForm, mcc: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">CAID</label>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    value={editForm.caid}
                    onChange={(e) => setEditForm({ ...editForm, caid: e.target.value })}
                  />
                </div>
                {/* SOUND-649: processor is CREATE-ONLY and is not offered here.
                    mids.processorId is an ACCESS-CONTROL key — routes/mids.mjs
                    scopes a processor account to { processorId: theirs } — so
                    editing it silently moves a MID between processors' visibility.
                    It also invalidates the midIntegrations bridges (316 external
                    ids issued BY the processor) and retroactively reattributes
                    risk, since mart_risk resolves processor through dim_sound_mid.
                    A merchant moving processor is a NEW MID, not an edited one.
                    Correcting a wrong selection deserves its own explicit action,
                    not a field beside "volume cap". */}
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Acquiring Bank</label>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    value={editForm.acquiringBank}
                    onChange={(e) => setEditForm({ ...editForm, acquiringBank: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">BIN</label>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    value={editForm.bin}
                    onChange={(e) => setEditForm({ ...editForm, bin: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">MID Number</label>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    value={editForm.mid}
                    onChange={(e) => setEditForm({ ...editForm, mid: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Effective Date</label>
                  <input
                    type="date"
                    className="input input-bordered w-full"
                    value={editForm.effectiveDate}
                    onChange={(e) => setEditForm({ ...editForm, effectiveDate: e.target.value })}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    The date this MID went live.
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Chargeback Threshold (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="input input-bordered w-full"
                    value={editForm.chargebackThreshold}
                    onChange={(e) => setEditForm({ ...editForm, chargebackThreshold: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Currencies</label>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    value={editForm.currencies}
                    onChange={(e) => setEditForm({ ...editForm, currencies: e.target.value })}
                    placeholder="USD, EUR"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">Comma separated.</p>
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="multiCurrency"
                    className="h-4 w-4 rounded border-border"
                    checked={editForm.multiCurrency}
                    onChange={(e) => setEditForm({ ...editForm, multiCurrency: e.target.checked })}
                  />
                  <label htmlFor="multiCurrency" className="text-sm font-medium text-foreground">
                    Multi-currency
                  </label>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Monthly Volume Cap ($)</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      className="input input-bordered w-full disabled:opacity-50"
                      placeholder="0"
                      value={editForm.isUncapped ? "" : editForm.monthlyVolumeCap}
                      disabled={editForm.isUncapped}
                      onChange={(e) =>
                        setEditForm({ ...editForm, monthlyVolumeCap: e.target.value })
                      }
                    />
                    <label className="flex items-center gap-1.5 whitespace-nowrap text-sm text-muted-foreground">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-border text-brand focus:ring-brand/40"
                        checked={editForm.isUncapped}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            isUncapped: e.target.checked,
                            ...(e.target.checked ? { monthlyVolumeCap: "" } : {}),
                          })
                        }
                      />
                      {UNCAPPED_LABEL}
                    </label>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Ownership</label>
                  <label className="flex items-center gap-2 text-sm text-muted-foreground">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-border text-brand focus:ring-brand/40"
                      checked={editForm.isSoundOwned}
                      onChange={(e) =>
                        setEditForm({ ...editForm, isSoundOwned: e.target.checked })
                      }
                    />
                    Sound-owned MID (we sourced it / earn the residual / own the bank relationship)
                  </label>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Tags (comma-separated)</label>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    value={editForm.tags}
                    onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-foreground mb-1 block">Notes</label>
                  <textarea
                    className="input input-bordered w-full"
                    rows={3}
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  />
                </div>
              </div>

              {/* Reserve sub-section */}
              <div className="border-t border-border pt-4">
                <h3 className="text-md font-semibold mb-3">Reserve</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1 block">Type</label>
                    <select
                      className="select select-bordered w-full"
                      value={editForm.reserveType}
                      onChange={(e) => setEditForm({ ...editForm, reserveType: e.target.value })}
                    >
                      <option value="none">None</option>
                      <option value="rolling">Rolling</option>
                      <option value="upfront">Upfront</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1 block">Percentage</label>
                    <input
                      type="number"
                      className="input input-bordered w-full"
                      value={editForm.reservePercentage}
                      onChange={(e) => setEditForm({ ...editForm, reservePercentage: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1 block">Hold Period (days)</label>
                    <input
                      type="number"
                      className="input input-bordered w-full"
                      value={editForm.reserveHoldPeriod}
                      onChange={(e) => setEditForm({ ...editForm, reserveHoldPeriod: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1 block">Cap ($)</label>
                    <input
                      type="number"
                      className="input input-bordered w-full"
                      value={editForm.reserveCap}
                      onChange={(e) => setEditForm({ ...editForm, reserveCap: e.target.value })}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-foreground mb-1 block">Reserve Notes</label>
                    <input
                      type="text"
                      className="input input-bordered w-full"
                      value={editForm.reserveNotes}
                      onChange={(e) => setEditForm({ ...editForm, reserveNotes: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
              <button
                className="btn btn-ghost"
                onClick={() => setEditing(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSaveEdit}
                disabled={submitting}
              >
                <Save className="h-4 w-4" />
                {submitting ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default requireAuth(MidDetail);
